import { useState, useEffect } from "react";
import classNames from "classnames";
import { get, set, unset } from "lodash";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import dynamic from "next/dynamic";

import useSaveWorkspaceConfig from "./hooks/useSaveWorkspaceConfig";
import Button from "../common/Button";
import Toggle from "../common/Toggle";
import Checkbox from "../common/Checkbox";
import Question from "./Question";
import Sidebar from "./Sidebar";
import ConfigurationForm from "./ConfigurationForm";
import FreeText from "./PdfReports/FreeText";
import scPageContent from "./SecondaryCapture/mask.json";
import pdfPageContent from "./PdfReports/pdf-data.json";
import Toast from "../Toast";
import Modal from "../common/Modal";
import TextareaInput from "../FormComponents/TextareaInput";
import { ConfigPreview } from "./ConfigList";
import SelectedTags from "./SelectedTags";

export const SecondaryCapture = dynamic(() => import("./SecondaryCapture"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center bold text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-900 w-full h-full">
      Loading...
    </div>
  ),
});

const PreviewModal = ({ isOpen, onCloseModal, onSave, answers, name, savingConfig }) => {
  return (
    <Modal
      title="Preview"
      onClose={onCloseModal}
      show={isOpen}
      className={classNames(
        "flex flex-col w-full lg:w-7/12  h-auto !max-h-[90%] overflow-auto"
      )}
    >
      <div className="p-4">
        <ConfigPreview answers={answers} name={name} editDraft={false} />
      </div>
      <div className="flex items-center flex-row justify-end p-4 border-t border-t-gray-600">
        <Button
          isPrimary
          disabled={savingConfig}
          onClick={() => onSave(name)}
          className="sm:mt-0 grow-1 ml-4 w-full sm:w-40"
        >
          {savingConfig ? "Saving..." : "Save"}
        </Button>
      </div>
    </Modal>
  );
};

const NameModal = ({ isOpen, onCloseModal, name, setName, setIsPreviewModalOpen, setIsNameModelOpen }) => {
  return (
    <Modal
      title="Name the drafted config"
      onClose={onCloseModal}
      show={isOpen}
      className={classNames(
        "flex flex-col w-full sm:w-7/12 lg:w-6/12 xl:w-4/12 h-auto !max-h-[90%] overflow-auto"
      )}
    >
      <div className="p-4">
        <TextareaInput
          value={name}
          onChange={(value) => setName(value)}
          placeholder="Enter name here"
        />
      </div>
      <div className="flex items-center flex-row justify-end p-4 border-t border-t-gray-600">
        <Button
          onClick={() => setIsNameModelOpen(false)}
          className="sm:mt-0 grow-1 ml-4 w-full sm:w-40"
        >
          Cancel
        </Button>
        <Button
          isPrimary
          disabled={!name}
          onClick={() => setIsPreviewModalOpen(true)}
          className="sm:mt-0 grow-1 ml-4 w-full sm:w-40"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

const questions = [
  {
    key: "general.organization",
    type: "checkbox",
    question: "What is the nature of your organization?",
    options: [
      "Primary Health Center",
      "Hospital (Secondary or Tertiary center)",
      "Diagnostic Center",
      "Teleradiology",
      "Research institute",
    ],
  },
  {
    key: "general.modality",
    type: "radio",
    question: "Which modality would Qure's AI run on?",
    options: ["Chest X-Ray (qXR)"],
  },
  {
    key: "general.careArea",
    type: "checkbox",
    question: "Where will the X-rays be used?",
    options: ["ER", "ICU", "OPD", "Health check-up", "GP Clinic", "Mobile Van/Devices"],
  },
  {
    key: "general.languages",
    type: "radio_dropdown",
    question: "Which language will you consume qXR outputs in?",
    options: ["English", "Spanish", "French"],
  },
  {
    key: "general.primaryUsers",
    type: "checkbox",
    question: "Who will be the primary users of qXR outputs?",
    options: [
      "Radiologists",
      "General Physicians",
      "Specialist Physicians",
      "Frontline Staff",
    ],
  },
];

const ConfigForm = (props) => {
  const { configMeta, setShowBigModal, onClose } = props;
  const steps = ["GENRAL", "CONFIGURATIONS", "SC Report", "PDF Report"];
  const [currentStep, setCurrentStep] = useState(0);
  const [isNameModelOpen, setIsNameModelOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const saveWorkspaceConfig = useSaveWorkspaceConfig();
  const [name, setName] = useState(get(configMeta, "name", ""));

  // config on the start of the form
  const initialAnswers = {
    general: {
      languages: "English",
    },
    reports: {
      output_service: "node_api",
      type: "free_text",
      enabled_sc_thumbnail: true,
      signature_text: "Radiologist Sign",
      bottom_disclaimer_text:
        "This is an auto-generated report by the software and cannot be used as the final report. Please treat this as a provisional report and correlate clinically. For any discrepancy please consult a Radiologist/ Chest Physician.",
    },
    sc: {
      output_service: "node_api",
      cxr_sc_with_sidebar: true,
      sidebar_hidden_tags: [],
      cxr_sc_with_topbar: true,
      cxr_sc_with_title: true,
      sc_report_not_found_enabled: true,
      cxr_sc_disclaimer: "Preview image, not for diagnostic use",
      cxr_sc_with_mask_borders: true,
      cxr_sc_with_mask_labels: true,
      enabled_tube_rendering: false,
      cxr_sc_with_bottombar: true,
      enable_disclaimer: true,
      enable_bottombar_logo: true,
      enable_version_info: true,
      enable_quantification: true,
      enable_ctr_line: false,
      
    },
  };

  const metaAnswers = get(configMeta, "answers", initialAnswers);
  // this is the variable which will store final config
  const [answers, setAnswers] = useState(metaAnswers);
  const [croppedCanvasUrl, setCroppedCanvasUrl] = useState(null);
  const [savingConfig, setSavingConfig] = useState(false);

  // this is the function to change the source_config
  const changeAnswer = (questionKey, answer, clearQuestionKeys = []) => {
    const newAnswers = { ...answers };
    clearQuestionKeys.map((key) => {
      unset(newAnswers, key);
    });
    set(newAnswers, questionKey, answer);
    setAnswers(newAnswers);
  };
  // final api call to save the config
  const onSave = (name) => {
    const configId = get(configMeta, "id", undefined);
    setSavingConfig(true);
    saveWorkspaceConfig
      .mutateAsync({
        ai_flow: answers,
        name: name,
      })
      .then(() => {
        setSavingConfig(false);
        Toast.success("Workspace Config saved successfully.");
        onClose();
      })
      .catch(() => {
        setSavingConfig(false);
        Toast.error("Unable to save config settings. Please try again.");
      });
  };

  useEffect(() => {
    if (currentStep >= 2) {
      setShowBigModal(true);
    } else {
      setShowBigModal(false);
    }
  }, [currentStep]);

  const renderStep = (step) => {
    switch (step) {
      case 0:
        return (
          <div className="h-[30rem] max-h-[60vh] overflow-y-auto bg-gray-100 dark:bg-gray-900 p-6 rounded-xl">
            {questions.map((question, i) => (
              <Question
                key={question.key}
                questionData={question}
                serialNumber={i + 1}
                changeAnswer={changeAnswer}
                answer={get(answers, question.key, null)}
              />
            ))}
          </div>
        );
      case 1:
        return (
          <div className="flex justify-between h-[30rem] max-h-[60vh] overflow-y-auto bg-gray-100 dark:bg-gray-900 p-6 rounded-xl">
            <ConfigurationForm answers={answers} changeAnswer={changeAnswer} configMeta={configMeta} />
          </div>
        );

      case 2:
        return (
          <div className="h-[30rem] max-h-[60vh] overflow-y-auto flex px-6">
            <SecondaryCapture
              pageContent={scPageContent}
              answers={get(answers, "sc", {})}
              croppedCanvasUrl={croppedCanvasUrl}
              setCroppedCanvasUrl={setCroppedCanvasUrl}
            />
          </div>
        );

      case 3:
        return (
          <div className="h-[30rem] max-h-[55vh] overflow-y-auto flex p-6">
            <div className="px-6 w-[50%] text-gray-800 border-r border-gray-600">
              <div className={classNames("py-6 px-8 w-full h-full", {})}>
                <div className="bg-gray-100 h-full">
                  <div className="text-left scale-[0.45] translate-x-[-18%] translate-y-[-27%]">
                    <FreeText pageContent={pdfPageContent} answers={answers} />
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 flex-1">
              <div
                className={classNames("py-6 px-8 h-full", {
                  "opacity-50": !get(answers, "reports.enabled_sc_thumbnail", false),
                })}
              >
                <div className="bg-gray-100 p-6 h-full">
                  <img
                    className="bg-gray-900 h-full w-full"
                    src={"/images/RibCavity.png"}
                    alt="test"
                  />
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div className="flex">
        <div className="p-6 flex-1">
          <div className="mx-10 my-4 flex border-t border-gray-700 relative justify-between">
            {steps.map((step, i) => (
              <div
                key={step}
                className={classNames(
                  "text-md -translate-y-3 first:-translate-x-1/2 last:translate-x-1/2",
                  {
                    "text-gray-500": currentStep !== i,
                  }
                )}
              >
                <div
                  className={classNames(
                    {
                      "bg-teal-300": currentStep === i,
                      "bg-teal-600": currentStep !== i,
                    },
                    "w-5 h-5 rounded-full mx-auto mb-2"
                  )}
                />
                {step}
              </div>
            ))}
          </div>
          {renderStep(currentStep)}
          <div className="flex mt-4">
            {currentStep > 0 ? (
              <button
                onClick={() => {
                  currentStep > 0 ? setCurrentStep(currentStep - 1) : null;
                }}
                className="flex justify-center items-center text-gray-600 dark:text-gray-400"
              >
                <ChevronLeftIcon className="h-5 w-5 mx-3" /> Back
              </button>
            ) : null}
            {currentStep < steps.length - 1 ? (
              <button
                onClick={() => {
                  currentStep < steps.length - 1 ? setCurrentStep(currentStep + 1) : null;
                }}
                className="flex justify-center items-center text-gray-600 dark:text-gray-400 ml-auto"
              >
                Next <ChevronRightIcon className="h-5 w-5 mx-3" />
              </button>
            ) : null}
          </div>
        </div>
        <Sidebar step={currentStep} changeAnswer={changeAnswer} answers={answers} />
      </div>
      {currentStep == 3 &&
        <div className="flex items-center flex-row justify-end p-4 border-t border-t-gray-600">
          <Button
            isPrimary
            onClick={() => setIsNameModelOpen(true)}
            className="sm:mt-0 grow-1 ml-4"
          >
            Save changes
          </Button>
        </div>
      }
      <NameModal
        isOpen={isNameModelOpen}
        onCloseModal={() => setIsNameModelOpen(false)}
        setIsPreviewModalOpen={setIsPreviewModalOpen}
        setIsNameModelOpen={setIsNameModelOpen}
        name={name}
        setName={setName}
      />
      <PreviewModal
        isOpen={isPreviewModalOpen}
        onCloseModal={() => setIsPreviewModalOpen(false)}
        name={name}
        onSave={onSave}
        answers={answers}
        savingConfig={savingConfig}
      />
    </>
  );
};

export default ConfigForm;
